<template>
  <div>
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-3" color="primary">mdi-cog</v-icon>
      Настройка главной страницы
      <v-spacer></v-spacer>
      <v-btn
        color="success"
        @click="saveSettings"
        :loading="saving"
      >
        <v-icon left>mdi-content-save</v-icon>
        Сохранить изменения
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-alert
        v-if="hasChanges"
        type="info"
        dense
        class="mb-4"
      >
        У вас есть несохранённые изменения
      </v-alert>

      <v-row>
        <v-col cols="12" lg="7">
          <v-card outlined>
            <v-card-title class="d-flex align-center">
              <v-icon class="mr-2" color="primary">mdi-view-grid</v-icon>
              Новости и памятки
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                outlined
                small
                @click="addBlock"
              >
                <v-icon left>mdi-plus</v-icon>
                Добавить блок
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-list dense class="pa-0">
                <v-list-item
                  v-for="(block, index) in paginatedBlocks"
                  :key="block.id || index"
                  class="pa-0 mb-2"
                >
                  <ArticleBlock
                    :block="block"
                    :index="index"
                    @change="markAsChanged"
                    @remove="removeBlock(index)"
                  />
                </v-list-item>
              </v-list>

              <v-pagination
                v-if="pageCount > 1"
                v-model="currentPage"
                :length="pageCount"
                class="mt-4"
                total-visible="5"
              ></v-pagination>

              <v-alert
                v-if="homePageBlocks.length === 0"
                type="info"
                class="mt-4"
              >
                Нет добавленных новостей. Нажмите "Добавить блок" для создания первой новости.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" lg="5">
          <v-card outlined>
            <v-card-title class="d-flex align-center">
              <v-icon class="mr-2" color="primary">mdi-eye</v-icon>
              Предварительный просмотр
            </v-card-title>
            <v-card-text>
              <ArticlePreview :blocks="homePageBlocks" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ArticleBlock from './components/ArticleBlock.vue';
import ArticlePreview from './components/ArticlePreview.vue';

export default {
  name: 'HomePageSettings',
  components: {
    ArticleBlock,
    ArticlePreview,
  },
  data () {
    return {
      saving: false,
      hasChanges: false,
      homePageBlocks: [],
      roleOptions: [
        { text: 'Администратор', value: 'admin' },
        { text: 'Админ-инженер', value: 'admin-engineer' },
        { text: 'Менеджер', value: 'manager' },
        { text: 'Агент', value: 'agent' },
        { text: 'Кол-центр', value: 'call-center' },
        { text: 'Мастер', value: 'master' },
        { text: 'Администратор КПП', value: 'checkpoint-admin' },
      ],
      currentPage: 1,
      itemsPerPage: 3,
    };
  },
  computed: {
    ...mapGetters('Articles', ['articles']),
    pageCount () {
      return Math.ceil(this.homePageBlocks.length / this.itemsPerPage);
    },
    paginatedBlocks () {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.homePageBlocks.slice(start, end);
    },
  },
  created () {
    this.loadSettings();
  },
  methods: {
    ...mapActions('Articles', [
      'loadArticles',
      'createArticle',
      'updateArticle',
      'deleteArticle',
      'deleteAllArticles',
    ]),
    async loadSettings () {
      this.saving = true;
      try {
        await this.loadArticles();

        this.homePageBlocks = [...this.articles].sort((a, b) => b.id - a.id);
        this.hasChanges = false;
      } catch (e) {
        this.$flash_danger('Ошибка загрузки статей');
      } finally {
        this.saving = false;
      }
    },
    prepareArticlePayload (block) {
      const { type, id, imageFile, image, created_at, updated_at, ...rest } = block;
      if (imageFile instanceof File || (image && typeof image === 'string' && image.length > 0)) {
        const formData = new FormData();
        formData.append('type', type);
        Object.entries(rest).forEach(([key, value]) => {
          formData.append(`data[${key}]`, value ?? '');
        });
        if (imageFile instanceof File) {
          formData.append('data[image]', imageFile);
        } else if (image) {
          formData.append('data[image]', image);
        }
        return formData;
      } else {
        return { type, data: rest };
      }
    },
    async saveSettings () {
      this.saving = true;
      try {
        await this.loadArticles();
        const existing = this.articles;
        const filledBlocks = this.homePageBlocks.filter(block => block.title && block.title.trim() !== '');

        await Promise.all(filledBlocks.map(async (block) => {
          const payload = this.prepareArticlePayload(block);
          if (block.id) {
            await this.updateArticle({ id: block.id, data: payload });
          } else {
            await this.createArticle(payload);
          }
        }));

        const filledIds = filledBlocks.filter(b => b.id).map(b => b.id);
        const toDelete = existing.filter(article => !filledIds.includes(article.id));
        await Promise.all(toDelete.map(article => this.deleteArticle(article.id)));

        this.$flash_success('Настройки успешно сохранены');
        this.hasChanges = false;
        await this.loadSettings();
      } catch (error) {
        console.error('Ошибка сохранения:', error);
        this.$flash_danger('Ошибка при сохранении настроек');
      } finally {
        this.saving = false;
      }
    },
    removeBlock (index) {
      this.homePageBlocks.splice(index, 1);
      this.markAsChanged();
    },
    addBlock () {
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      const formattedDate = `${day}.${month}.${year}`;

      const allRoles = this.roleOptions.map(role => role.value);

      this.homePageBlocks.unshift({
        title: 'Заголовок',
        description: 'Краткое описание',
        date: formattedDate,
        phone: '',
        image: '',
        imageFile: null,
        icon: 'mdi-information',
        color: 'primary',
        type: 'news',
        roles: allRoles,
        enabled: true,
      });
      this.markAsChanged();
    },
    markAsChanged () {
      this.hasChanges = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.preview-container {
  max-height: 500px;
  overflow-y: auto;
}

.preview-block {
  display: flex;
  flex-direction: column;
  padding: 16px;
  margin-bottom: 16px;
  border-radius: 12px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }

  .preview-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;

    .preview-icon {
      font-size: 24px;
      margin-right: 12px;
    }

    .preview-title {
      font-size: 16px;
      font-weight: 600;
    }
  }

  .preview-content {
    .preview-description {
      margin: 0 0 12px 0;
      font-size: 14px;
      color: #666;
      line-height: 1.4;
    }

    .preview-date {
      display: flex;
      align-items: center;
      font-size: 14px;
      color: #666;
      font-weight: 500;
    }
    .preview-phone {
      display: flex;
      align-items: center;
      font-size: 14px;
      color: #666;
      font-weight: 500;
    }
    .preview-type {
      margin-top: 8px;
    }
  }
}

.preview-block--primary {
  border-left: 4px solid #1976d2;
  .preview-icon { color: #1976d2; }
}

.preview-block--success {
  border-left: 4px solid #4caf50;
  .preview-icon { color: #4caf50; }
}

.preview-block--warning {
  border-left: 4px solid #ff9800;
  .preview-icon { color: #ff9800; }
}

.preview-block--error {
  border-left: 4px solid #f44336;
  .preview-icon { color: #f44336; }
}

.preview-block--purple {
  border-left: 4px solid #9c27b0;
  .preview-icon { color: #9c27b0; }
}

.preview-block--teal {
  border-left: 4px solid #009688;
  .preview-icon { color: #009688; }
}

.w-100 {
  width: 100%;
}

.block-card {
  transition: all 0.2s ease;
}
</style>
